"use client";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import {
  addDataLayerProductsAddList,
  addDataLayerProductToList,
} from "@/helpers/dataLayer";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { useLogin } from "@/helpers/useCommonFunctions";
import {
  analyticsAddToCartEventAfterSearch,
  analyticsAddToCartEventBeforeSearch,
} from "@/services/algolia/analytics";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import {
  setActiveCartList,
  setCartItemListData,
  updateCartShoppingList,
} from "@/utils/redux/cartProduct";
import { setManageList, setUserData } from "@/utils/redux/manageList";
import { addProductCategories } from "@/utils/redux/manageList/actions";
import { setItemListData } from "@/utils/redux/shoppingList";
import { addListItemsToCart } from "@/utils/redux/shoppingList/actions";
import { Product, SingleItem } from "@/utils/types/product";
import { useGigya, useGigyaAccount } from "@dxp-feed/gigya";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Box,
  Text,
  Input,
} from "@dxp-feed/ui";
import Link from "@/components/Link";
import { addOffersToList } from "@/services/mulesoft";
import { updateFavouriteOffers } from "@/utils/redux/favOffers";
import { setAlert } from "@/utils/redux/gigya/scene";
import { set } from "date-fns";
import { CustomToast } from "../CustomToast";

interface ProductLoginModalProps {
  loginModalOpen: boolean;
  loginModalClose: (status: boolean) => void;
  showQuantityText?: boolean;
  createLinkClass?: string;
  popUpBodyText?: string;
  productToBeAdded?: any;
  onQuantityUpdate?: any;
  isHash?: any;
  addOffersToStorage?: () => void;
  listStatus?: (status: boolean) => void;
  item?: any;
  openDialog?: any;
  offerCart?: boolean;
  recipeName?: string;
  storeId?: string;
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
}

const ProductLoginModal: React.FC<ProductLoginModalProps> = ({
  loginModalOpen,
  loginModalClose,
  showQuantityText,
  createLinkClass,
  popUpBodyText,
  productToBeAdded,
  onQuantityUpdate,
  isHash,
  addOffersToStorage,
  listStatus,
  item,
  openDialog,
  offerCart = false,
  recipeName,
  storeId,
  isGlobalSearch = false,
  setDropDown,
}) => {
  const [modalState, setModalState] = useState<{
    canAddList: boolean;
    isModalOpen: boolean;
  }>({
    canAddList: false,
    isModalOpen: false,
  });
  const [listName, setListName] = useState<string>("");
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const { favOffers } = useAppSelector((state) => state.favoirateOffersSlice);
  const [isModalLoginOpen, setIsModalLoginOpen] = useState(loginModalOpen);
  const [error, setError] = useState(false);
  const [message, setmessage] = useState("");
  const { gigya, isLoaded } = useGigya();
  const dispatch = useAppDispatch();
  const { isAuthorized } = useGigyaAccount({ gigya, isLoaded });
  const { loggedIn: userLoggedIn, uid: Uid } = useAppSelector(
    (state) => state.gigyaslice
  );
  const gigyaToken = useGigyaAccountInfo();
  const { shoppingListAemData }: any = useAppSelector(
    (state) => state?.getShoppingListDataSlice
  );
  const { loadLogin } = useLogin();
  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );
  const handleLoginClose = () => {
    setIsModalLoginOpen(false);
    loginModalClose(false);
    setModalState({ isModalOpen: false, canAddList: false });
    setListName("");
    document.body.style.touchAction = "";
    if (isGlobalSearch && setDropDown) {
      setDropDown(true);
    }
    setError(false);
    setmessage("");
  };

  const globalSearch = useAppSelector((state) => state.algoliaSlice.search);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const wordCount = value.trim().length;
    const regex = /^[\p{L}\p{N}\s]*$/u;

    if (!regex.test(value.trim())) {
      setError(true);
      setmessage("Enter a valid input.");
    } else if (wordCount >= 50) {
      setError(true);
      setmessage("Character count shuld be less than 50 characters!");
    } else {
      setError(false);
      setmessage("");
    }
    setListName(value);
    if (addOffersToStorage) {
      addOffersToStorage();
    }
  };

  const dataLayerAddList = (name: string) => {
    addDataLayerProductsAddList({
      event: "button_click",
      click_text: "create a list",
      list_name: name,
      page_title: document.title,
    });
    sendSalesforceInteraction({
      name: "listCreated", //listDeleted
      banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      isAnonymous: Uid ? 0 : 1,
      gigyaUid: Uid,
      listName: name,
      screenName: document.title,
      screenClass: window.location.href,
      utmSource: getUTMParameter("utm_source"),
      utmMedium: getUTMParameter("utm_medium"),
      utmCampaign: getUTMParameter("utm_campaign"),
    });
  };
  const insertManagementList = async () => {
    try {
      setIsButtonClicked(true);
      if (recipeName) {
        const recipeNameObj = {
          recipeName: [recipeName],
        };
        productToBeAdded = {
          ...productToBeAdded,
          meta:
            recipeName && recipeName != "" ? JSON.stringify(recipeNameObj) : "",
        };
      }

      if (!listName) {
        setModalState({ isModalOpen: false, canAddList: false });
        handleLoginClose();
        return;
      }
      if (listName === "Favourites") {
        setModalState({ isModalOpen: false, canAddList: false });
        handleLoginClose();
        return;
      }
      dataLayerAddList(listName);
      if (userLoggedIn) {
        const response = await gigyaToken();

        if (response.idToken) {
          const data = {
            user_id: Uid,
            name: listName.replace(/\s{2,}/g, " ").trim(),
            userToken: response.idToken,
          };
          await dispatch(addProductCategories(data));
          if (offerCart) {
            const newOffer = {
              offer_id: productToBeAdded.offerId,
              banner: process.env.NEXT_PUBLIC_BANNER as string,
              offer_type: productToBeAdded.offerType,
              offer_description: JSON.stringify(productToBeAdded),
            };
            const oldOffers = favOffers?.favouriteOffers || [];
            const updatedOffers = [...oldOffers, newOffer]; // Append new offer
            dispatch(updateFavouriteOffers(updatedOffers));
            addOffersToList(newOffer, response.idToken);
            loginModalClose && loginModalClose(false);
            openDialog && openDialog();
            return;
          }

          if (Object.keys(productToBeAdded).length > 0) {
            productToBeAdded.itemId = productToBeAdded.item_id;
            productToBeAdded.userToken = response.idToken;
            productToBeAdded.storeId = storeId;
            const result = await dispatch(addListItemsToCart(productToBeAdded));
            if (result) {
              const activeList = managementListData?.itemsList?.filter(
                (item: { is_active: number }) => item.is_active === 1
              );
              productToBeAdded.insertId = result?.payload?.resultData?.id;
              productToBeAdded.categoryId =
                result?.payload?.resultData?.category_id ?? activeList?.[0]?.id;
              productToBeAdded.name = listName;
              productToBeAdded.deletedId = result?.payload?.deletedId;
              dispatch(updateCartShoppingList(productToBeAdded));
            }
          }
        } else {
          console.log("No ID token found");
        }
      } else {
        dispatch(
          setManageList({
            id: Date.now().toString(),
            user_id: "",
            name: listName.replace(/\s{2,}/g, " ").trim(),
            createdAt: new Date(),
            sorting: "byDepartment",
            isAuthenticated: userLoggedIn,
          })
        );
        dispatch(
          setActiveCartList({
            name: listName.replace(/\s{2,}/g, " ").trim(),
            id: Date.now().toString(),
            items: [],
          })
        );

        if (Object.keys(productToBeAdded).length > 0) {
          dispatch(setItemListData(productToBeAdded));
          dispatch(setCartItemListData(productToBeAdded));
          onQuantityUpdate();
        }
      }

      if (Object.keys(productToBeAdded).length > 0) {
        if (
          globalSearch?.queryValue?.length &&
          globalSearch.queryID?.products?.length
        ) {
          analyticsAddToCartEventAfterSearch({
            objectIDs: [String(productToBeAdded.item_id)],
            userToken: Uid ?? undefined,
            queryID: globalSearch.queryID.products,
            quantity: 1,
          });
        } else {
          analyticsAddToCartEventBeforeSearch({
            objectIDs: [String(productToBeAdded.item_id)],
            userToken: Uid ?? undefined,
            quantity: 1,
          });
        }

        addToCartAnalytics(item, 1);
      }

      setModalState({ ...modalState, canAddList: false });

      setModalState({ isModalOpen: false, canAddList: false });
      setListName("");

      handleLoginClose();
      if (listStatus) {
        listStatus(true);
      }
    } catch (err: any) {
      setModalState({ ...modalState, canAddList: false });
      setListName("");
      handleLoginClose();
      console.log(err);
      /* empty */
    } finally {
      setIsButtonClicked(false);
    }
  };

  const addToCartAnalytics = (item: Product, quantity: number) => {
    const promotionValues = item?.promotions && Object.values(item.promotions);
    const hasPromotionReward = promotionValues?.[0]?.promotionReward;
    const includesY =
      hasPromotionReward?.[0]?.membershipLevelText?.includes("Y");

    let offerTag = "";
    if (includesY) {
      offerTag = "Mass offer - Scene+";
    } else if (hasPromotionReward) {
      offerTag = "Mass offer - Non Scene+";
    }

    addDataLayerProductToList({
      event: `${item?.allergens?.alcohol ? "add_liquor_product_to_list" : "add_products_to_list"}`,
      product_name: item.name,
      item_list_name: listName,
      list_name: item?.hierarchicalCategories?.lvl0?.[0] ?? "",
      page_title: document.title,
      offer_tag: offerTag,
    });
    sendSalesforceInteraction({
      name: "cartItem",
      banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
      cartItemStatus: "Add Item to List",
      currency: "$",
      price: item?.price ?? 0,
      quantity: quantity,
      catalogObjectId: `${item?.articleNumber}`,
      catalogObjectType: item.name ?? "",
      isAnonymous: Uid ? 0 : 1,
      gigyaUid: Uid,
      cartEventId: "",
      screenName: document.title,
      screenClass: window.location.href,
      utmSource: getUTMParameter("utm_source"),
      utmMedium: getUTMParameter("utm_medium"),
      utmCampaign: getUTMParameter("utm_campaign"),
    });
  };

  const toggleModal = () => {
    setModalState({ ...modalState, canAddList: true });
    setListName("");
    console.log("create clicked");
    setIsModalLoginOpen(true);
  };

  const setUserAsLoggedIn = () => {
    if (isLoaded && modalState?.canAddList === false) {
      loadLogin("login");
    }
  };
  function handleClose(): void {
    console.log("Close message.");
    handleLoginClose();
    if (isGlobalSearch && setDropDown) {
      setDropDown(true);
    }
  }
  return (
    <>
      {modalState?.canAddList || userLoggedIn ? (
        <Dialog
          isOpen={isModalLoginOpen}
          onClose={handleLoginClose}
          className="px-0 py-4 max-w-[432px] md:max-w-[464px]"
          positionClass="px-5"
        >
          <DialogHeader className="border-0 [&_button]:text-grey950 [&_button]:w-[24px] [&_button]:h-[24px] [&_button]:text-[14px] px-6">
            <Text as="h2" className="text-base font-bold">
              {shoppingListAemData?.items?.[0]?.addNewShoppingListLabel ||
                "Add New Shopping List"}
            </Text>
            <Button
              className="text-gray-500 font-bold text-lg"
              onClick={() => {
                handleLoginClose();
                setModalState({ ...modalState, canAddList: false });
              }}
              aria-label="Close modal"
              variant="icon"
              size="none"
            >
              <Text
                as="span"
                className="icon-close cursor-pointer text-grey950 text-[14px] w-[24px] h-[24px]"
              ></Text>
            </Button>
          </DialogHeader>
          <DialogBody className="mt-0 px-6">
            <Text as="p" className="font-normal text-body mb-1">
              <label htmlFor="createListInput">
                {shoppingListAemData?.items?.[0]?.nameYourShoppingListLabel ||
                  "Name your shopping list"}
              </label>
            </Text>
            <Input
              id="createListInput"
              name="createListInput"
              onChange={handleInputChange}
              value={listName}
              type="text"
              placeholder={
                shoppingListAemData?.items?.[0]?.addShoppingListPlaceholder ||
                "e.g Sunday BBQ"
              }
              maxLength={60}
              autoComplete="off"
              className="w-full h-14 border-grey350 rounded py-1 placeholder:text-brandBlack focus-visible:outline-brandBlack"
            />
            {error && <p className="text-red-500 text-sm">{message}</p>}
          </DialogBody>
          <DialogFooter className="w-full border-0 justify-self-end items-center mt-0 px-6">
            <Link
              href="#"
              onClick={(e) => {
                handleLoginClose();
                e.preventDefault();
                setModalState({ ...modalState, canAddList: false });
              }}
              className="text-body text-brandSecondary font-bold underline"
              aria-label="Click here to cancel"
            >
              {shoppingListAemData?.items?.[0]?.cancelLabel || "Cancel"}
            </Link>
            <Button
              onClick={insertManagementList}
              className={`w-auto h-[40px] text-brandWhite !text-body font-normal !px-[25.5px] !rounded-[100px] flex-auto sm:flex-none ml-3.5 ${listName?.trim().length == 0 || error || isButtonClicked ? "text-grey550 hover:text-grey550" : ""}`}
              aria-label="Click here to save the list"
              disabled={
                error || listName?.trim().length == 0 || isButtonClicked
              }
            >
              {shoppingListAemData?.items?.[0]?.saveLabel || "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : (
        // <Dialog
        //   className="px-0 py-4 max-w-[432px] md:max-w-[464px]"
        //   positionClass="px-5"
        //   isOpen={loginModalOpen}
        //   onClose={handleLoginClose}
        // >
        //   <DialogHeader className="border-0 px-6 items-start">
        //     <Text
        //       as="h4"
        //       className="text-base text-brandBlack font-bold max-w-[300px]"
        //     >
        //       {popUpBodyText ??
        //         (showQuantityText
        //           ? `${
        //               shoppingListAemData?.items?.[0]
        //                 ?.shoppingListLoginPrompt ||
        //               "Your shopping list won't be saved unless you're signed in. Want to continue without saving?"
        //             }`
        //           : "")}

        //       {!showQuantityText
        //         ? "Please log-in to add this item to your favourites"
        //         : ""}
        //     </Text>
        //     <Button
        //       className="text-grey950 min-w-[24px] w-[24px] h-[24px] text-[14px] justify-end ml-auto"
        //       aria-label="Close modal"
        //       variant="icon"
        //       size="none"
        //       onClick={() => {
        //         handleLoginClose();
        //       }}
        //     >
        //       <Text
        //         as="span"
        //         className="icon-close cursor-pointer text-grey950 text-[14px] w-[24px] h-[24px]"
        //       ></Text>
        //     </Button>
        //   </DialogHeader>
        //   <DialogFooter className="border-0 justify-end flex gap-[20px] items-center mt-4 py-0 text-center px-6">
        //     {showQuantityText && (
        //       <Box
        //         as="div"
        //         className={`mt-[14.5px] mb-[9.5px] ${!userLoggedIn && managementListData?.itemsList?.length ? "cursor-not-allowed" : ""}`}
        //       >
        //         <Link
        //           href="#"
        //           onClick={(e) => {
        //             e.preventDefault();
        //             toggleModal();
        //           }}
        //           className={`text-body text-brandSecondary font-bold underline ${!userLoggedIn && managementListData?.itemsList?.length ? "pointer-events-none opacity-50" : ""}`}
        //           aria-label={"Click here to create the list"}
        //           aria-disabled={
        //             !userLoggedIn && !!managementListData?.itemsList?.length
        //           }
        //           tabIndex={
        //             !userLoggedIn && managementListData?.itemsList?.length
        //               ? -1
        //               : 0
        //           }
        //         >
        //           {shoppingListAemData?.items?.[0]?.createAnywayLabel ||
        //             "Create Anyway"}
        //         </Link>
        //       </Box>
        //     )}
        //     <Box as="div">
        //       <Button
        //         onClick={() => setUserAsLoggedIn()}
        //         className={`w-auto h-[40px] !text-body text-white font-normal !px-[24px] !rounded-[100px] flex-auto sm:flex-none ${showQuantityText ? "" : "mb-[9.5px]"}`}
        //         aria-label="Click here to login"
        //       >
        //         {shoppingListAemData?.items?.[0]?.loginLabel || "Login"}
        //       </Button>
        //     </Box>
        //   </DialogFooter>
        // </Dialog>
        <CustomToast
          isGlobalSearch={isGlobalSearch}
          setDropDown={setDropDown}
          message={
            <Box as="div" className="flex items-start  4xs:gap-3 gap-1">
              <Box as="div" className="rounded-full flex-shrink-0">
                <Text
                  as="span"
                  className="demo-icon icon-sponsored text-brandBlack text-[18px] w-6 h-6 flex items-center justify-center"
                ></Text>
              </Box>
              <Box as="div" className="flex-grow flex flex-wrap">
                <Box as="div" className="w-full">
                  <Text
                    as="span"
                    className="font-primaryFont text-body text-brandBlack font-bold pr-7"
                  >
                    Looks like you're logged out
                  </Text>
                </Box>
                <Text as="p" className="mt-[9.5px] w-full text-body text-left">
                  <Button
                    className="text-body text-brandBlack font-bold underline rounded-none"
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        loadLogin("login");
                      }
                    }}
                    onClick={() => loadLogin("login")}
                    variant="icon"
                    size="none"
                    aria-label="Sign In"
                  >
                    Sign In
                  </Button>
                  <Text as="span" className="ml-1 text-body text-brandBlack">
                    to save your favourites list and come back to it anytime.
                  </Text>
                </Text>
              </Box>
            </Box>
          }
          bgColor={"error"}
          position="top"
          onClose={handleClose}
          autoCloseDuration={5000}
          closeButton={
            <Text
              as="span"
              className="demo-icon icon-close text-brandBlack cursor-pointer text-[14px]"
            ></Text>
          }
          className="font-normal flex z-[9999999] w-[90%] max-w-[503px] lg:w-auto !bg-[#FFD8D8] items-start"
        />
      )}
    </>
  );
};

export default ProductLoginModal;
